import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bot, Sparkles, X, MessageSquare, FileText, GraduationCap, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const pagePrompts = {
  '/community': [
    'Shape this into a mentor-ready question.',
    'Suggest people in my industry I should ask for help.',
    'Turn my job goal into chat filters.',
  ],
  '/marketplace': [
    'Pick the best tool for my next interview.',
    'Compare resume builder, prep, mentor, and background check.',
    'Help me decide what to buy first.',
  ],
  '/resume-builder': [
    'Improve my summary for this job.',
    'Make my bullet points measurable.',
    'Find missing skills for this role.',
  ],
  '/interview-prep': [
    'Generate hard interview questions.',
    'Coach my answer using STAR.',
    'Build a practice plan for this role.',
  ],
};

const defaultPrompts = [
  'Help me get ready for this job.',
  'Tell me what to do next on this page.',
  'Write a stronger message for a mentor.',
];

export default function AIAssistantDock() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const prompts = useMemo(() => {
    const match = Object.keys(pagePrompts).find((path) => location.pathname.startsWith(path));
    return match ? pagePrompts[match] : defaultPrompts;
  }, [location.pathname]);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[70] flex h-12 w-12 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800"
        aria-label="Open AI assistant"
        data-testid="ai-assistant-open"
      >
        <Bot size={22} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-[70] w-[min(360px,calc(100vw-32px))] border bg-white shadow-xl">
      <div className="flex items-center justify-between border-b bg-slate-900 px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-cyan-300" />
          <span className="font-semibold">AI Tools</span>
        </div>
        <button onClick={() => setOpen(false)} aria-label="Close AI assistant" data-testid="ai-assistant-close">
          <X size={18} />
        </button>
      </div>

      <div className="space-y-3 p-4">
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/marketplace')}>
            <ShoppingBag size={14} className="mr-1" /> Tools
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/community')}>
            <MessageSquare size={14} className="mr-1" /> Chat
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/resume-builder')}>
            <FileText size={14} className="mr-1" /> Resume
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/interview-prep')}>
            <GraduationCap size={14} className="mr-1" /> Prep
          </Button>
        </div>

        <div className="space-y-2">
          {prompts.map((prompt) => (
            <button
              key={prompt}
              onClick={() => setDraft(prompt)}
              className="w-full border px-3 py-2 text-left text-sm hover:bg-gray-50"
              data-testid="ai-assistant-prompt"
            >
              {prompt}
            </button>
          ))}
        </div>

        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask for role-specific coaching..."
          className="min-h-24"
          data-testid="ai-assistant-draft"
        />
      </div>
    </div>
  );
}
